import type { StageTemplates } from '~/lib/stage-templates'
import { keyBetween, orderKeyBound } from '~convex/lib/orderKey'
import type { ProjectType } from '~convex/lib/projectEnums'
import type { ProjectRow } from './projects'

/**
 * ONE BOARD PER PROJECT TYPE, stacked down the page.
 *
 * The projects board used to have STATUS columns, and the default catalog has
 * four keys with one hidden — so every live project sat in "Em andamento" and
 * the board was one tall column. Stage is the axis that actually moves.
 *
 * Stage cannot be one column set, though: execution templates are per
 * `projectType`, so Interiores runs Levantamento → Conceito → Detalhamento →
 * Entrega while Reforma runs the six-stage construction spine. Unioning them
 * would put two cards in one column that are NOT at the same place — 3rd of 4
 * is nearly done, 4th of 6 is halfway.
 *
 * So the templates stop being a wrinkle and become the page's structure: one
 * board per type, each rendering its own template's columns. Inside a board,
 * a column means exactly one thing.
 */

type ExecutionTemplate = StageTemplates['execution'][number]
export type StageDef = ExecutionTemplate['stages'][number]

/** The project fields the pure board model actually needs. */
export type ProjectBoardRow = {
  _id: string
  currentStageKey: string
  executionStages?: StageDef[]
  order?: string
  projectType?: ProjectType
}

/** How long a project may sit untouched before the board says so. */
export const STALE_AFTER_DAYS = 30
const DAY_MS = 86_400_000

/**
 * What the card's mark says, or `null` for a project with nothing to report.
 *
 * At most ONE mark per card, worst first: two badges on a 252px card is
 * noise, not signal. `awaiting_client` is deliberately absent for now — it is
 * the panel's `owner: 'client'` loop, which costs a per-project fan-out
 * across four tables and cannot be read off the project row.
 */
export type ProjectHealth = 'late' | 'stale' | null

/** The marks a filter can select. Same values, minus the "nothing" arm. */
export type ProjectHealthFilter = 'late' | 'stale'

/** Whitelist for the `?health` search param, in the column's own order. */
export const PROJECT_HEALTH_ORDER: ProjectHealthFilter[] = ['late', 'stale']

/**
 * The mark for one project.
 *
 * A closed project is never marked: a delivered job whose `endDate` slipped
 * is history, not a problem, and it would light up the Entrega column
 * permanently. `isDeliveryStage` covers the same case one step earlier — a
 * project sitting in Entrega has arrived, whatever the date says.
 */
export const projectHealth = ({
  isClosed,
  isDeliveryStage,
  now,
  project,
  today,
}: {
  isClosed: boolean
  isDeliveryStage: boolean
  now: number
  project: Pick<ProjectRow, 'endDate' | 'lastActivityAt'>
  /** `YYYY-MM-DD` in the viewer's own calendar. */
  today: string
}): ProjectHealth => {
  if (isClosed || isDeliveryStage) {
    return null
  }
  // Lexicographic comparison IS date comparison in `YYYY-MM-DD`.
  if (project.endDate !== undefined && project.endDate < today) {
    return 'late'
  }
  if (now - project.lastActivityAt > STALE_AFTER_DAYS * DAY_MS) {
    return 'stale'
  }
  return null
}

/** One stage column inside one board. */
export type StageColumn<Row extends ProjectBoardRow = ProjectRow> = {
  projects: Row[]
  stage: StageDef
}

/** One type's board: its template, its columns, and how many cards in all. */
export type TypeBoard<Row extends ProjectBoardRow = ProjectRow> = {
  columns: StageColumn<Row>[]
  count: number
  /** `null` is the "Sem tipo" board, which runs the generic spine. */
  projectType: ProjectType | null
  /** Stable key for React and for the collapse preference. */
  key: string
}

/**
 * Orders cards by their manual `order` key. Projects with no key sort last;
 * ties break on `_id` so two cards briefly sharing a key still order
 * deterministically. Same rule the status board used.
 */
const byOrderKey = (a: ProjectBoardRow, b: ProjectBoardRow): number => {
  if (a.order !== undefined && b.order !== undefined && a.order !== b.order) {
    return a.order < b.order ? -1 : 1
  }
  if (a.order === undefined && b.order !== undefined) {
    return 1
  }
  if (a.order !== undefined && b.order === undefined) {
    return -1
  }
  return a._id < b._id ? -1 : 1
}

/** A stable fractional key for a card's current slot, tolerant of old ties. */
export const projectOrderKeyAt = (
  projects: Pick<ProjectBoardRow, '_id' | 'order'>[],
  draggedId: string
): string => {
  const position = projects.findIndex((project) => project._id === draggedId)
  if (position === -1) {
    return keyBetween(null, null)
  }
  const before = orderKeyBound(projects[position - 1]?.order)
  let after = orderKeyBound(projects[position + 1]?.order)
  if (before !== null && (after === null || after <= before)) {
    after =
      projects
        .slice(position + 1)
        .map((project) => orderKeyBound(project.order))
        .find((candidate) => candidate !== null && candidate > before) ?? null
  }
  return keyBetween(before, after)
}

/** Whether the project's immutable execution snapshot accepts this stage. */
export const projectCanEnterStage = (
  project: Pick<ProjectBoardRow, 'currentStageKey' | 'executionStages'>,
  stageKey: string
): boolean =>
  stageKey === project.currentStageKey ||
  (project.executionStages?.some((stage) => stage.key === stageKey) ?? false)

/**
 * Buckets one type's projects into that template's columns.
 *
 * The stage list has already been merged with every project's immutable
 * snapshot, so a removed catalog stage keeps its own column. The first-column
 * fallback is only a final guard for malformed input; `mergedStagesFor`
 * normally guarantees a bucket for every `currentStageKey`.
 */
const columnsFor = <Row extends ProjectBoardRow>(
  projects: Row[],
  stages: StageDef[]
): StageColumn<Row>[] => {
  const byStage = new Map<string, Row[]>(stages.map((stage) => [stage.key, []]))
  const [first] = stages
  for (const project of projects) {
    const bucket = byStage.get(project.currentStageKey) ?? byStage.get(first?.key ?? '')
    bucket?.push(project)
  }
  return stages.map((stage) => ({
    projects: (byStage.get(stage.key) ?? []).sort(byOrderKey),
    stage,
  }))
}

/**
 * Merges the current catalog spine with every immutable project snapshot.
 *
 * The catalog is only a starting order. Existing projects can legitimately
 * carry removed or reordered stages in `executionStages`, and the backend
 * validates moves against that snapshot. Keeping every snapshot stage on the
 * board prevents an orphaned card from being mislabeled as the first stage,
 * while a newly-added catalog stage is still available to newer projects.
 */
const mergedStagesFor = <Row extends ProjectBoardRow>(
  projects: readonly Row[],
  catalogStages: StageDef[]
): StageDef[] => {
  const merged: StageDef[] = []
  const byKey = new Map<string, StageDef>()

  const mergeSequence = (sequence: StageDef[]) => {
    for (let index = 0; index < sequence.length; index++) {
      const stage = sequence[index]
      if (byKey.has(stage.key)) {
        continue
      }

      const nextKnown = sequence.slice(index + 1).find((candidate) => byKey.has(candidate.key))
      const previousKnown = [...sequence.slice(0, index)]
        .reverse()
        .find((candidate) => byKey.has(candidate.key))
      const insertAt = nextKnown
        ? merged.findIndex((candidate) => candidate.key === nextKnown.key)
        : previousKnown
          ? merged.findIndex((candidate) => candidate.key === previousKnown.key) + 1
          : merged.length
      merged.splice(insertAt, 0, stage)
      byKey.set(stage.key, stage)
    }
  }

  mergeSequence(catalogStages)
  for (const project of projects) {
    mergeSequence(project.executionStages ?? [])
  }

  // A legacy/corrupt row may predate snapshots. It still gets a visible
  // column instead of disappearing or pretending to be in the first stage;
  // the backend will continue to refuse moves until its snapshot is repaired.
  for (const project of projects) {
    if (!byKey.has(project.currentStageKey)) {
      const fallback: StageDef = {
        key: project.currentStageKey,
        name: project.currentStageKey,
        stageKind: 'execution',
      }
      merged.push(fallback)
      byKey.set(fallback.key, fallback)
    }
  }

  return merged
}

/**
 * The page: one board per type that HAS projects, in the catalog's own order.
 *
 * Ordered by the type catalog rather than by card count, deliberately. Count
 * ordering reshuffles the page every time a project is created or delivered,
 * and a board that moves under you while you are reading it is worse than one
 * that sits lower than you would have put it. "Sem tipo" is always last.
 *
 * Empty boards do not render at all: a studio that has never done a
 * regularização should not carry that board forever, and this is what keeps
 * the page the size of the work rather than the size of the enum.
 */
export const groupProjectsIntoBoards = <Row extends ProjectBoardRow>(
  projects: Row[],
  templates: StageTemplates | undefined,
  typeOrder: readonly ProjectType[]
): TypeBoard<Row>[] => {
  const stagesFor = new Map<ProjectType | null, StageDef[]>(
    (templates?.execution ?? []).map((entry) => [entry.projectType, entry.stages])
  )
  const generic = stagesFor.get(null) ?? []
  const byType = new Map<ProjectType | null, Row[]>()
  for (const project of projects) {
    const type = project.projectType ?? null
    const bucket = byType.get(type)
    if (bucket === undefined) {
      byType.set(type, [project])
    } else {
      bucket.push(project)
    }
  }

  const ordered: (ProjectType | null)[] = [...typeOrder, null]
  return ordered.flatMap((projectType) => {
    const rows = byType.get(projectType)
    if (rows === undefined || rows.length === 0) {
      return []
    }
    const catalogStages = (projectType === null ? generic : stagesFor.get(projectType)) ?? generic
    const stages = mergedStagesFor(rows, catalogStages)
    return [
      {
        columns: columnsFor(rows, stages),
        count: rows.length,
        // The board's stable handle: its type, or the no-type sentinel.
        key: projectType ?? 'none',
        projectType,
      },
    ]
  })
}

/** One selectable stage in the Stage filter: its key, and what to call it. */
export type StageOption = { key: string; name: string }

/**
 * Every execution stage a project on this page could be sitting in, once.
 *
 * The page is one kanban PER TYPE precisely because the templates disagree,
 * but a FILTER asks a different question: "show me everything in obra" spans
 * types on purpose. So the dimension is the union of the execution templates,
 * deduped by key and ordered exactly as the boards are, so the filter reads
 * down the page in the order the page itself runs.
 *
 * Two templates sharing a key is the normal case rather than a collision —
 * `entrega` means the same thing in every spine, which is what makes the
 * option worth having. When they disagree on the NAME the first one wins: a
 * one-word difference in a filter label, against dropping the option or
 * offering the same key twice.
 *
 * Pre-contract stages are absent because pre-contract projects are: this list
 * shows won projects only, and a lead lives on the funnel board.
 */
export const executionStageOptions = (
  templates: StageTemplates | undefined,
  typeOrder: readonly ProjectType[],
  projects: readonly ProjectBoardRow[] = []
): StageOption[] => {
  if (templates === undefined && projects.length === 0) {
    return []
  }
  const stagesFor = new Map<ProjectType | null, StageDef[]>(
    (templates?.execution ?? []).map((entry) => [entry.projectType, entry.stages])
  )
  const generic = stagesFor.get(null) ?? []
  const projectsFor = new Map<ProjectType | null, ProjectBoardRow[]>()
  for (const project of projects) {
    const projectType = project.projectType ?? null
    const rows = projectsFor.get(projectType)
    if (rows === undefined) {
      projectsFor.set(projectType, [project])
    } else {
      rows.push(project)
    }
  }
  const seen = new Set<string>()
  const options: StageOption[] = []
  for (const projectType of [...typeOrder, null]) {
    const catalogStages = (projectType === null ? generic : stagesFor.get(projectType)) ?? generic
    // Use the same catalog + immutable-snapshot merge as the board. A stage an
    // admin later removes stays filterable for as long as a visible project
    // still carries it, and it sits in the same relative position in both UI
    // surfaces. During template loading the snapshots are sufficient alone.
    const stages = mergedStagesFor(projectsFor.get(projectType) ?? [], catalogStages)
    for (const stage of stages) {
      if (seen.has(stage.key)) {
        continue
      }
      seen.add(stage.key)
      options.push({ key: stage.key, name: stage.name })
    }
  }
  return options
}
